"use client";

import Link from "next/link";

import type { Brand } from "@/shared/api/services/productService";

import { ScrollCarousel } from "@/features/home/components/ScrollCarousel";
import { useHomePopularBrands } from "@/features/home/queries/useHomeData";

import { Skeleton } from "@/components/ui/skeleton";

export function PopularBrandsSection() {
  const { data, isLoading } = useHomePopularBrands();
  const brands: Brand[] = data?.data?.brands ?? [];

  if (!isLoading && brands.length === 0) return null;

  return (
    <section className="py-10 md:py-14 border-t border-neutral-100">
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="mb-6 md:mb-8">
          <h2 className="font-heading text-base md:text-lg font-semibold tracking-tight">
            인기 브랜드
          </h2>
        </div>

        {isLoading ? (
          <div className="flex gap-5 overflow-hidden">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex-shrink-0 w-[26%] md:w-[calc(16.666%-17px)]">
                <Skeleton className="aspect-square w-full rounded-full" />
                <Skeleton className="h-3 w-2/3 mx-auto mt-3" />
              </div>
            ))}
          </div>
        ) : (
          <ScrollCarousel className="gap-5 md:gap-6">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                href={`/brands/${brand.id}`}
                className="group flex-shrink-0 w-[26%] sm:w-[18%] md:w-[calc(16.666%-20px)] flex flex-col items-center"
              >
                <div className="aspect-square w-full rounded-full overflow-hidden bg-neutral-100 relative">
                  {brand.thumbnail ? (
                    <img
                      src={brand.thumbnail}
                      alt={brand.name}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">
                      {brand.name.charAt(0)}
                    </div>
                  )}
                </div>
                <p className="mt-2.5 text-xs text-neutral-500 text-center truncate w-full group-hover:text-neutral-900 transition-colors">
                  {brand.name}
                </p>
              </Link>
            ))}
          </ScrollCarousel>
        )}
      </div>
    </section>
  );
}
